/**
 * slack-mrkdwn — converters for Slack's "mrkdwn" subset and Block-Kit blocks
 * into standard markdown so a generic markdown renderer can display persisted
 * brief content.
 *
 * Why this exists: Slack uses its own subset of markdown — single-asterisk
 * bold, single-underscore italic, angle-bracket-pipe links, special tokens
 * for users / channels / mentions. A persisted brief (Block-Kit JSON or
 * mrkdwn text) round-tripped through `slackBlocksToMarkdown` /
 * `slackMrkdwnToMarkdown` produces a string close enough to standard
 * markdown that downstream renderers / `whitespace-pre-wrap` divs render it
 * sensibly without bespoke parsing.
 *
 * Conversion rules implemented (Slack mrkdwn → markdown):
 *   *bold*                  → **bold**
 *   _italic_                → *italic*
 *   ~strike~                → ~~strike~~
 *   <URL|label>             → [label](URL)
 *   <URL>                   → <URL>            (already valid markdown)
 *   <@USERID>               → @USERID
 *   <#CID|channelname>      → #channelname
 *   <#CID>                  → #CID
 *   <!here> / <!channel>    → @here / @channel / @everyone
 *   line-leading "&gt;"     → "> "             (Slack-escaped blockquote)
 *
 * Left unchanged on purpose:
 *   - Triple-backtick fences (already valid markdown).
 *   - Emoji shortcodes (`:smile:`) — renderer can decide.
 *   - Bullet markers `•` — most renderers treat as plain text but readable.
 */

/**
 * Convert Slack mrkdwn (a single string) to standard markdown.
 *
 * Implementation note: every transform is regex-based on the full string in
 * order. We don't tokenize — Slack mrkdwn is shallow enough that running the
 * substitutions in the right order is correct for the inputs we encounter
 * (brief text, feedback text, user-typed slash-command output). The order
 * matters where one transform's output could otherwise be re-matched by a
 * later one (e.g. converting `<URL|label>` first so `<URL>` doesn't grab the
 * pipe-form too).
 */
export function slackMrkdwnToMarkdown(input: string): string {
  if (typeof input !== 'string' || input.length === 0) return '';

  let out = input;

  // 1. Special tokens: <!here>, <!channel>, <!everyone>. Done before generic
  //    angle-bracket handling so the bang form isn't swallowed as a URL.
  out = out.replace(/<!(here|channel|everyone)(?:\|[^>]*)?>/g, '@$1');

  // 2. User mentions: <@U12345> or <@U12345|label>. Strip to @USERID — we do
  //    not have a directory lookup at render time.
  out = out.replace(/<@([A-Z0-9]+)(?:\|[^>]*)?>/g, '@$1');

  // 3. Channel refs: <#C123|name> → #name; <#C123> → #C123.
  out = out.replace(/<#([A-Z0-9]+)\|([^>]+)>/g, '#$2');
  out = out.replace(/<#([A-Z0-9]+)>/g, '#$1');

  // 4. Linked URLs with label: <https://x|y> → [y](https://x). Be permissive
  //    about the URL portion — Slack accepts any non-`>` non-`|` chars.
  out = out.replace(/<([^|>\s]+)\|([^>]+)>/g, '[$2]($1)');

  // 5. Bare URLs in angle brackets: <https://x> — leave intact. Markdown
  //    treats `<https://x>` as a valid autolink.

  // 6. Line-leading blockquote: Slack escapes `>` to `&gt;` at the start of a
  //    line. Convert to standard markdown blockquote. Be careful: only at
  //    start-of-line, never mid-text where `&gt;` might be content. We DON'T
  //    rewrite mid-line `&gt;` because that's almost always a comparison
  //    operator or HTML escape, not Slack markup.
  out = out.replace(/(^|\n)&gt;\s?/g, '$1> ');

  // 7. Bold: Slack `*bold*` → `**bold**`. The trick is avoiding *italic
  //    inside markdown* style markers. Our heuristic: a `*…*` run is bold
  //    iff it doesn't contain a newline AND the asterisks aren't part of a
  //    longer run. We accept that some users may type asterisk-as-emphasis
  //    in a way that bleeds across paragraphs — those cases are rare in
  //    Slack-originated text.
  out = out.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1**$2**');

  // 8. Strikethrough: ~text~ → ~~text~~. Don't match URLs or things with
  //    spaces around the tilde — keep it tight.
  out = out.replace(/(^|[^~])~([^~\n]+?)~(?!~)/g, '$1~~$2~~');

  // 9. Italic: Slack `_italic_` is preserved as-is — most CommonMark
  //    renderers accept underscore italic. Skipping the transform avoids
  //    accidentally mangling identifiers like snake_case_thing.

  return out;
}

// ---------------------------------------------------------------------------
// Block-Kit → markdown
// ---------------------------------------------------------------------------

/**
 * Loose typing for Slack Block-Kit blocks. We type the fields we read; every
 * other field stays `unknown` so unknown block types pass through cleanly
 * to the placeholder branch.
 */
export interface SlackBlock {
  type: string;
  text?: { type?: string; text?: string };
  elements?: unknown[];
  fields?: Array<{ type?: string; text?: string }>;
  // Block-Kit also has `accessory`, `image_url`, `alt_text`, etc. — read
  // ad-hoc inside the converter so this surface stays minimal.
  [key: string]: unknown;
}

interface ContextElement {
  type?: string;
  text?: string;
}

interface ImageBlock extends SlackBlock {
  image_url?: string;
  alt_text?: string;
  title?: { text?: string };
}

/**
 * Render a Block-Kit `blocks` array to a flat markdown-ish string. Best
 * effort — interactive elements are surfaced as a `*[buttons]*` placeholder
 * so the reader knows something interactive lived there. Emits paragraphs
 * separated by blank lines.
 */
export function slackBlocksToMarkdown(blocks: unknown[]): string {
  if (!Array.isArray(blocks)) return '';

  const out: string[] = [];

  for (const raw of blocks) {
    if (!raw || typeof raw !== 'object') continue;
    const block = raw as SlackBlock;

    switch (block.type) {
      case 'section': {
        // section.text — emit straight (mrkdwn or plain_text both treated as
        // mrkdwn for the converter; plain_text passes through harmlessly).
        if (block.text?.text) {
          out.push(slackMrkdwnToMarkdown(block.text.text));
        }
        // section.fields — Slack renders 2-up; we emit one per line.
        if (block.fields && Array.isArray(block.fields)) {
          for (const field of block.fields) {
            if (field?.text) out.push(slackMrkdwnToMarkdown(field.text));
          }
        }
        break;
      }

      case 'header': {
        const text = block.text?.text ?? '';
        out.push(`## ${slackMrkdwnToMarkdown(text)}`);
        break;
      }

      case 'divider': {
        out.push('---');
        break;
      }

      case 'actions': {
        // Buttons / select menus don't render in a static view — leave a
        // placeholder so the reader knows something interactive lived here.
        out.push('*[buttons]*');
        break;
      }

      case 'context': {
        // Render each element on its own dim quoted line.
        const elements = (block.elements as ContextElement[] | undefined) ?? [];
        for (const el of elements) {
          if (el?.text) {
            out.push(`> ${slackMrkdwnToMarkdown(el.text)}`);
          }
        }
        break;
      }

      case 'image': {
        const img = block as ImageBlock;
        const alt = img.alt_text ?? img.title?.text ?? '';
        const url = img.image_url ?? '';
        if (url) out.push(`![${alt}](${url})`);
        break;
      }

      default: {
        out.push(`*[unsupported block: ${block.type}]*`);
        break;
      }
    }
  }

  return out.join('\n\n');
}
